import { Chunk } from '../world/chunk';
import { NoiseGenerator } from './noise';
import { getHeight, getTemperature, getHumidity } from './surface';
import { isCave } from './caves';
import { getOre } from './ores';
import { generateOakTree, generateBirchTree, TreeBlock } from './trees';
import { getBiome, BiomeType } from '../world/biome';
import { BlockId } from '@shared/block-ids';
import { CHUNK_WIDTH, CHUNK_DEPTH, SEA_LEVEL } from '@shared/chunk-constants';
import { seedRandom } from '../utils/math';

export class TerrainGenerator {
  private noise: NoiseGenerator;
  private treeNoise: NoiseGenerator;
  private seed: number;

  constructor(seed: number) {
    this.seed = seed;
    this.noise = new NoiseGenerator(seed);
    this.treeNoise = new NoiseGenerator(seed + 9999);
  }

  generate(chunk: Chunk): void {
    const wx0 = chunk.cx * CHUNK_WIDTH;
    const wz0 = chunk.cz * CHUNK_DEPTH;

    // First pass: terrain shape
    for (let x = 0; x < CHUNK_WIDTH; x++) {
      for (let z = 0; z < CHUNK_DEPTH; z++) {
        const wx = wx0 + x;
        const wz = wz0 + z;

        const height = getHeight(this.noise, wx, wz);
        const temp = getTemperature(this.noise, wx, wz);
        const humid = getHumidity(this.noise, wx, wz);
        const biome = getBiome(temp, humid, height);

        // Bedrock
        chunk.setBlock(x, 0, z, BlockId.BEDROCK);

        // Stone fill
        for (let y = 1; y < height - 4; y++) {
          if (!isCave(this.noise, wx, y, wz)) {
            const ore = getOre(this.noise, wx, y, wz);
            chunk.setBlock(x, y, z, ore ?? BlockId.STONE);
          }
        }

        // Filler layers
        for (let y = Math.max(1, height - 4); y < height; y++) {
          if (!isCave(this.noise, wx, y, wz)) {
            chunk.setBlock(x, y, z, biome.fillerBlock);
          }
        }

        // Surface block
        if (height > 0) {
          if (height <= SEA_LEVEL) {
            chunk.setBlock(x, height, z, biome.underwaterBlock);
          } else {
            chunk.setBlock(x, height, z, biome.topBlock);
          }
        }

        // Water fill
        for (let y = height + 1; y <= SEA_LEVEL; y++) {
          if (chunk.getBlock(x, y, z) === BlockId.AIR) {
            chunk.setBlock(x, y, z, BlockId.WATER);
          }
        }

        // Decorations
        if (height > SEA_LEVEL) {
          const rng = seedRandom(wx * 73856093 ^ wz * 19349663 ^ this.seed);

          // Grass/flowers
          if (biome.grassDensity > 0 && rng() < biome.grassDensity) {
            chunk.setBlock(x, height + 1, z, BlockId.TALL_GRASS);
          } else if (biome.flowerDensity > 0 && rng() < biome.flowerDensity) {
            chunk.setBlock(x, height + 1, z, rng() > 0.5 ? BlockId.FLOWER_RED : BlockId.FLOWER_YELLOW);
          }
        }
      }
    }

    // Tree pass
    this.placeTrees(chunk, wx0, wz0);

    chunk.generated = true;
    chunk.dirty = true;
  }

  private placeTrees(chunk: Chunk, wx0: number, wz0: number): void {
    // Check a wider area to catch trees whose trunks are in neighboring chunks but whose leaves extend here
    for (let x = -2; x < CHUNK_WIDTH + 2; x++) {
      for (let z = -2; z < CHUNK_DEPTH + 2; z++) {
        const wx = wx0 + x;
        const wz = wz0 + z;

        const treeVal = this.treeNoise.get2D(wx * 0.5, wz * 0.5);
        if (treeVal < 0.75) continue;

        const height = getHeight(this.noise, wx, wz);
        if (height <= SEA_LEVEL) continue;

        const temp = getTemperature(this.noise, wx, wz);
        const humid = getHumidity(this.noise, wx, wz);
        const biome = getBiome(temp, humid, height);
        if (biome.treeDensity <= 0) continue;

        const rng = seedRandom(wx * 48611 ^ wz * 97813 ^ this.seed);
        if (rng() > biome.treeDensity * 5) continue;

        const isBirch = biome.type === BiomeType.BIRCH_FOREST;
        const treeSeed = wx * 31337 ^ wz * 73856 ^ this.seed;
        const treeBlocks: TreeBlock[] = isBirch
          ? generateBirchTree(treeSeed)
          : generateOakTree(treeSeed);

        for (const block of treeBlocks) {
          const bx = x + block.dx;
          const by = height + 1 + block.dy;
          const bz = z + block.dz;

          // Only place if within this chunk
          if (bx >= 0 && bx < CHUNK_WIDTH && bz >= 0 && bz < CHUNK_DEPTH) {
            const existing = chunk.getBlock(bx, by, bz);
            // Don't overwrite solid blocks with leaves
            if (block.id === BlockId.LEAVES_OAK || block.id === BlockId.LEAVES_BIRCH) {
              if (existing === BlockId.AIR || existing === BlockId.TALL_GRASS ||
                  existing === BlockId.FLOWER_RED || existing === BlockId.FLOWER_YELLOW) {
                chunk.setBlock(bx, by, bz, block.id);
              }
            } else {
              chunk.setBlock(bx, by, bz, block.id);
            }
          }
        }
      }
    }
  }
}
